import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import CustomModal from '../../components/atoms/CustomModal';
import InputSelect from '../../components/atoms/InputSelect';
import CustomDatePicker from '../../components/atoms/CustomDatePicker';
import Button from '../../components/atoms/Button';
import { useGetSupportTicketStatusesQuery } from '../../store/services/lookups';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  statusFilter?: string;
  setStatusFilter?: (val: string) => void;
  dateFrom?: any;
  setDateFrom?: (val: any) => void;
  dateTo?: any;
  setDateTo?: (val: any) => void;
  customerTypes?: string[];
  setCustomerTypes?: (val: string[]) => void;
  appliedFiltersCount?: number;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: statusesData, isLoading: isLoadingStatuses } =
    useGetSupportTicketStatusesQuery();

  // Internal draft states to allow cancel without applying
  const [draftStatus, setDraftStatus] = useState<string>('');
  const [draftCustomerType, setDraftCustomerType] = useState<string>('');
  const [draftDateFrom, setDraftDateFrom] = useState<Dayjs | null>(null);
  const [draftDateTo, setDraftDateTo] = useState<Dayjs | null>(null);

  // Sync draft states from URL params whenever modal opens
  useEffect(() => {
    if (isOpen) {
      const statusParam =
        searchParams.get('statusIds') || searchParams.get('status') || '';
      const matched = (statusesData || []).find(
        (item: { id: string; code: string }) =>
          item.id === statusParam || item.code === statusParam
      );
      setDraftStatus(matched ? matched.id : statusParam);

      setDraftCustomerType(searchParams.get('customerType') || '');

      const fromParam =
        searchParams.get('dateFrom') || searchParams.get('fromDate');
      setDraftDateFrom(fromParam ? dayjs(fromParam) : null);

      const toParam =
        searchParams.get('dateTo') || searchParams.get('toDate');
      setDraftDateTo(toParam ? dayjs(toParam) : null);
    }
  }, [isOpen, searchParams, statusesData]);

  const statusOptions = (statusesData || []).map(
    (item: { id: string; code: string; name: string }) => ({
      label: t(`support.${item.code}`, item.name),
      value: item.id,
    })
  );

  const customerTypeOptions = [
    {
      label: t('support.customerTypes.business_buyer', 'Business buyer'),
      value: 'BUSINESS_BUYER',
    },
    {
      label: t('support.customerTypes.business_seller', 'Business seller'),
      value: 'BUSINESS_SELLER',
    },
    {
      label: t('support.customerTypes.individual_buyer', 'Individual buyer'),
      value: 'INDIVIDUAL_BUYER',
    },
  ];

  const appliedDraftCount = [
    draftStatus,
    draftCustomerType,
    draftDateFrom,
    draftDateTo,
  ].filter(Boolean).length;

  const handleReset = () => {
    setDraftStatus('');
    setDraftCustomerType('');
    setDraftDateFrom(null);
    setDraftDateTo(null);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('statusIds');
    nextParams.delete('status');
    nextParams.delete('customerType');
    nextParams.delete('dateFrom');
    nextParams.delete('dateTo');
    nextParams.delete('fromDate');
    nextParams.delete('toDate');
    nextParams.delete('page');
    setSearchParams(nextParams);
  };

  const handleSave = () => {
    const nextParams = new URLSearchParams(searchParams);

    if (draftStatus) {
      nextParams.set('statusIds', draftStatus);
    } else {
      nextParams.delete('statusIds');
    }
    nextParams.delete('status');

    if (draftCustomerType) {
      nextParams.set('customerType', draftCustomerType);
    } else {
      nextParams.delete('customerType');
    }

    if (draftDateFrom) {
      nextParams.set('dateFrom', dayjs(draftDateFrom).format('YYYY-MM-DD'));
    } else {
      nextParams.delete('dateFrom');
    }
    nextParams.delete('fromDate');

    if (draftDateTo) {
      nextParams.set('dateTo', dayjs(draftDateTo).format('YYYY-MM-DD'));
    } else {
      nextParams.delete('dateTo');
    }
    nextParams.delete('toDate');

    // Reset pagination to first page when filters are applied
    nextParams.delete('page');

    setSearchParams(nextParams);
    onClose();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('support.filter', 'Filter')}
      description={
        <span className="text-gray-500 font-normal text-sm flex gap-1">
          {t('support.filterApplied', {
            count: appliedDraftCount,
            defaultValue: `${appliedDraftCount} filter applied`,
          })}{' '}
          <span
            className="text-[#20A144] cursor-pointer hover:underline"
            onClick={handleReset}
          >
            {t('support.resetFilter', 'Reset filter')}
          </span>
        </span>
      }
      width={600}
    >
      <div className="flex flex-col gap-6 mt-2">
        {/* Status Section */}
        <div className="flex flex-col gap-2">
          <h4 className="font-bold text-gray-900 text-[15px]">
            {t('support.status', 'Status')}
          </h4>
          <InputSelect
            placeholder={t('support.selectStatus', 'Select status')}
            options={statusOptions}
            value={draftStatus || undefined}
            onChange={(val) => setDraftStatus((val as string) || '')}
            disabled={isLoadingStatuses}
            allowClear
          />
        </div>

        {/* Customer Type Section */}
        <div className="flex flex-col gap-2">
          <h4 className="font-bold text-gray-900 text-[15px]">
            {t('support.customerType', 'Customer type')}
          </h4>
          <InputSelect
            placeholder={t('support.selectCustomerType', 'Select customer type')}
            options={customerTypeOptions}
            value={draftCustomerType || undefined}
            onChange={(val) => setDraftCustomerType((val as string) || '')}
            allowClear
          />
        </div>

        {/* Dates Section */}
        <div className="grid grid-cols-2 gap-4">
          <CustomDatePicker
            id="date-from"
            label={t('support.dateFrom', 'Date from')}
            labelClassName="!font-bold !text-[15px] !text-gray-900"
            placeholder="dd/mm/yyyy"
            value={draftDateFrom}
            onChange={(val: any) => setDraftDateFrom(val)}
            disabledDate={(current: Dayjs) =>
              draftDateTo ? current.isAfter(draftDateTo, 'day') : false
            }
          />
          <CustomDatePicker
            id="date-to"
            label={t('support.dateTo', 'Date to')}
            labelClassName="!font-bold !text-[15px] !text-gray-900"
            placeholder="dd/mm/yyyy"
            value={draftDateTo}
            onChange={(val: any) => setDraftDateTo(val)}
            disabledDate={(current: Dayjs) =>
              draftDateFrom ? current.isBefore(draftDateFrom, 'day') : false
            }
          />
        </div>

        {/* Actions */}
        <div className="flex justify-center items-center gap-4 mt-6">
          <Button
            variant="outline"
            className="!rounded-2xl px-8 !h-[45px] w-32 font-semibold border-gray-300 hover:opacity-70"
            onClick={onClose}
          >
            {t('support.cancel', 'Cancel')}
          </Button>
          <Button
            variant="dark"
            className="!rounded-2xl px-8 !h-[45px] w-32 font-semibold !bg-[#0E0E0E] !text-white hover:!opacity-80"
            onClick={handleSave}
          >
            {t('support.save', 'Save')}
          </Button>
        </div>
      </div>
    </CustomModal>
  );
};

export default FilterModal;
